import { useQuery } from '@apollo/client';
import React, { FC, useState } from 'react';
import Tabs from '@atlaskit/tabs';
import { ProxyGraphClients } from '@atlassian/temp-client-utils';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { FetchTaskDetailQuery, GroupMember, Task, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ErrorComponents } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { withApollo } from '@css-xp/base/graphql-provider';
import ApproveModal from '../../modal/approve-modal';
import CancelRequestModal from '../../modal/cancel-request-modal';
import ChangeAssigneeModal from '../../modal/change-assignee-modal';
import ClaimModal from '../../modal/claim-modal';
import CommentModal from '../../modal/comment-modal';
import DeclineModal from '../../modal/decline-modal';
import ReplyModal from '../../modal/reply-modal';
import RequestReasonModal from '../../modal/request-reason-modal';
import UnclaimModal from '../../modal/unclaim-modal';
import { fetchTaskDetail } from '../../queries/tasks';
import { ActivityLogTab, CommentsTab, TaskDetailTab } from '../../tabs';
import { TaskActionButtons } from '../../task-actions/task-action-buttons';
import { TaskAction } from '../../task-actions/task-actions';
import './task-detail-page.scss';

interface RequestProps {
  taskId: string;
  closeSidebar: () => void;
  refetch?: () => void;
  refetchAll?: () => void;
}

export const TaskDetailPage: FC<RequestProps> = React.memo(({ taskId, refetch, refetchAll, closeSidebar }) => {
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);
  const [selectedGroupMember, setSelectedGroupMember] = useState<GroupMember | undefined>(undefined);

  const user = useApplicationContext();
  const userId = user?.userId?.split('@')?.[0];

  const { hasTaskManagerWriteAccess } = useApprovalDeskPermissions();

  const { data, loading, error } = useQuery<FetchTaskDetailQuery>(fetchTaskDetail, {
    skip: !userId,
    variables: {
      taskId,
      id: userId,
    },
  });

  const task: Task = React.useMemo(
    () => ({
      businessKey: data?.taskDetail?.businessKey,
      taskNumber: data?.taskDetail?.taskVariables?.taskNumber,
      approvalStatus: data?.taskDetail?.taskVariables?.approvalStatus,
      assigneeId: data?.taskDetail?.taskProperties?.assignee,
      initiatorId: data?.taskDetail?.processVariables?.initiatorId,
      ownerId: data?.taskDetail?.taskProperties?.owner,
      requestType: data?.taskDetail?.processVariables?.requestType,
      slackChannel: data?.taskDetail?.processVariables?.approvalEntityResult?.slackChannel,
      taskId,
      processInstanceId: data?.taskDetail?.taskProperties?.processInstanceId,
      entityType: data?.taskDetail?.processVariables?.approvalEntityResult?.entityType,
      slackMessageUrl: data?.taskDetail?.taskVariables?.slackMessageUrl,
      entityId: data?.taskDetail?.processVariables?.approvalEntityResult?.entityId,
      discountApprovalRequiredInPricingPlan: data?.taskDetail?.processVariables?.discountApprovalRequiredInPricingPlan,
    }),
    [data, taskId]
  );

  // TODO: need to extract this to a common place to be consumed by both task list and task detail
  const getModal = React.useCallback(
    (action: TaskAction) => {
      switch (action) {
        case TaskAction.APPROVE:
          return <ApproveModal task={task} closeModal={closeSidebar} onComplete={refetch} />;
        case TaskAction.DECLINE:
          return <DeclineModal task={task} closeModal={closeSidebar} onComplete={refetch} />;
        case TaskAction.REQUEST_REASONS:
          return <RequestReasonModal task={task} closeModal={closeSidebar} onComplete={refetch} />;
        case TaskAction.REPLY:
          return <ReplyModal task={task} closeModal={closeSidebar} onComplete={refetch} />;
        case TaskAction.CANCEL_REQUEST:
          return <CancelRequestModal task={task} closeModal={closeSidebar} onComplete={refetch} />;
        case TaskAction.UNASSIGN_TASK:
          return <UnclaimModal task={task} closeModal={closeSidebar} onComplete={refetchAll} />;
        case TaskAction.ASSIGN_TASK:
          return <ClaimModal task={task} closeModal={closeSidebar} onComplete={refetchAll} />;
        // case TaskAction.COMMENT:
        //   content = (
        //     <>
        //       <h2>{ModalTitle.COMMENT}</h2>
        //       <br />
        //       <CommentModal task={task} closeModal={closeSidebar} />
        //     </>
        //   );
        //   break;
        case TaskAction.CHANGE_ASSIGNEE:
          return (
            <ChangeAssigneeModal
              taskList={[
                {
                  groupId: data?.taskDetail?.processVariables?.approvalEntityResult?.approverGroup as string,
                  requestType: data?.taskDetail?.processVariables?.requestType,
                  slackMessageUrl: task?.slackMessageUrl as string,
                  slackChannel: task?.slackChannel as string | undefined,
                  taskId,
                },
              ]}
              assignee={selectedGroupMember?.userId as string}
              onComplete={refetch}
              closeModal={closeSidebar}
              assigneeName={selectedGroupMember?.name as string}
            />
          );
      }
      return null;
    },
    [refetchAll, refetch, task, selectedGroupMember]
  );

  const handleClick = React.useCallback(
    (action: TaskAction) => {
      const content = getModal(action);
      if (content) {
        setModalContent(content);
      }
    },
    [getModal, selectedGroupMember]
  );

  const tabs = React.useMemo(() => {
    return [
      {
        label: 'Details',
        content: data?.taskDetail && (
          <TaskDetailTab
            openModal={handleClick}
            selectedGroupMember={selectedGroupMember}
            userRole={data.userRole}
            onSelectGroupMember={setSelectedGroupMember}
            taskDetail={data.taskDetail}
          />
        ),
      },
      {
        label: 'Comments',
        content: <CommentsTab taskId={taskId} />,
      },
      {
        label: 'Activity log',
        content: <ActivityLogTab taskId={taskId} />,
      },
    ];
  }, [taskId, data, selectedGroupMember]);

  if (loading) return <LoadingIndicator />;

  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  if (!data?.taskDetail) {
    return null;
  }

  return (
    <div className="task-detail-wrapper" data-testid="task-detail-page">
      {modalContent ? (
        modalContent
      ) : (
        <>
          <h2>{`Request ${task.taskNumber}`}</h2>
          {data?.taskDetail?.processVariables?.initiatorComments && (
            <>
              <h5 className="description-heading">About the request</h5>
              <p className="description">{data.taskDetail.processVariables.initiatorComments}</p>
            </>
          )}
          {hasTaskManagerWriteAccess && (
            <TaskActionButtons task={task} userRole={data.userRole} onClick={handleClick} />
          )}
          <div className="task-detail-tabs">
            <Tabs tabs={tabs} />
          </div>
        </>
      )}
    </div>
  );
});

TaskDetailPage.displayName = 'TaskDetailPage';

export default withApollo(TaskDetailPage, ProxyGraphClients['APD']);
